<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { CsEmptyState } from '../../empty-state';
import { CsIcon } from '../../icon';
import { CsInput } from '../../input';
import { CsListbox, CsListboxItem } from '../../listbox';
import {
  CsPopover,
  CsPopoverCard,
  CsPopoverCardContent,
  CsPopoverContent,
  CsPopoverTrigger,
} from '../../popover';
import { CsText } from '../../text';
import { toolbarButtonItemRecipe } from './toolbarRecipe';

/**
 * @typedef {object} CannedResponseProps
 * @property {boolean} [isDisabled=false] - Whether the canned response picker button is disabled
 * @property {('default'|'inverse')} [variant='default'] - Color variant for the icon
 * @property {Array} [cannedResponses=[]] - Array of canned responses with {id, short_code, content} structure
 */

/**
 * @type {CannedResponseProps}
 */
const props = defineProps({
  isDisabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'default',
  },
  cannedResponses: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['select']);

const search = ref('');
const searchInput = ref(null);
const highlightedIndex = ref(0);
const listContainerRef = ref(null);
const isPopoverOpen = ref(false);

// Filter canned responses based on search
const filteredCannedResponses = computed(() => {
  if (!search.value) {
    return props.cannedResponses;
  }

  const searchTerm = search.value.toLowerCase();
  return props.cannedResponses.filter(
    response =>
      response.short_code.toLowerCase().includes(searchTerm) ||
      response.content?.toLowerCase().includes(searchTerm)
  );
});

const hasResults = computed(() => filteredCannedResponses.value.length > 0);

function adjustScrollPosition() {
  const container = listContainerRef.value;
  const selectedElement = container?.querySelector('[data-highlighted="true"]');

  if (container && selectedElement) {
    const containerRect = container.getBoundingClientRect();
    const elementRect = selectedElement.getBoundingClientRect();

    if (elementRect.top < containerRect.top) {
      selectedElement.scrollIntoView({ block: 'center' });
    } else if (elementRect.bottom > containerRect.bottom) {
      selectedElement.scrollIntoView({ block: 'center' });
    }
  }
}

// Reset highlighted index when filtered results change
watch(filteredCannedResponses, () => {
  highlightedIndex.value = 0;
  nextTick(() => adjustScrollPosition());
});

function onSelectCannedResponse(response) {
  emit('select', response);
  isPopoverOpen.value = false;
}

function handleKeyDown(event) {
  if (!hasResults.value) return;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      highlightedIndex.value = Math.min(
        filteredCannedResponses.value.length - 1,
        highlightedIndex.value + 1
      );
      nextTick(() => adjustScrollPosition());
      break;
    case 'ArrowUp':
      event.preventDefault();
      highlightedIndex.value = Math.max(0, highlightedIndex.value - 1);
      nextTick(() => adjustScrollPosition());
      break;
    case 'Enter':
      event.preventDefault();
      if (filteredCannedResponses.value[highlightedIndex.value]) {
        onSelectCannedResponse(
          filteredCannedResponses.value[highlightedIndex.value]
        );
      }
      break;
    case 'Escape':
      event.preventDefault();
      isPopoverOpen.value = false;
      break;
    default:
      break;
  }
}

function handleOpenPopover() {
  isPopoverOpen.value = true;
  search.value = '';
  highlightedIndex.value = 0;
  nextTick(() => {
    searchInput.value?.focus();
  });
}

function handleClosePopover() {
  isPopoverOpen.value = false;
  search.value = '';
  highlightedIndex.value = 0;
}
</script>

<template>
  <CsPopover
    is-manual
    :is-open="isPopoverOpen"
    is-teleport
    class="flex"
    @open="handleOpenPopover"
    @close="handleClosePopover"
  >
    <CsPopoverTrigger @click="handleOpenPopover">
      <button
        v-cs-tooltip="'Canned response'"
        data-component="CsEditorToolbarCannedResponse"
        :class="
          toolbarButtonItemRecipe({
            ...props,
            isActive: isPopoverOpen,
          })
        "
        class="gap-1 !w-auto !px-1.5"
        :disabled="props.isDisabled"
      >
        <CsIcon name="chat-referral" />

        Canned response
      </button>
    </CsPopoverTrigger>

    <CsPopoverContent>
      <CsPopoverCard>
        <CsPopoverCardContent class="w-[400px] min-w-[400px] max-w-[400px]">
          <div class="p-2">
            <CsText weight="semibold" class="mb-1"> Canned response </CsText>

            <CsInput
              ref="searchInput"
              v-model="search"
              placeholder="Search canned responses..."
              @keydown="handleKeyDown"
            />
          </div>

          <CsEmptyState
            v-if="!hasResults"
            icon-type="no-result-found"
            heading="No results found"
            content="Your search didn't return any results."
            class="py-8"
          />

          <div
            v-else
            ref="listContainerRef"
            class="max-h-[300px] overflow-y-auto"
          >
            <CsListbox>
              <CsListboxItem
                v-for="(response, index) in filteredCannedResponses"
                :key="response.id"
                :is-manual-highlight="true"
                :is-hovered="index === highlightedIndex"
                :is-highlighted="index === highlightedIndex"
                @mouseenter="highlightedIndex = index"
                @click="onSelectCannedResponse(response)"
              >
                <CsIcon name="chat-referral" class="flex-none" />

                <div class="min-w-0 overflow-hidden">
                  <CsText weight="medium" class="truncate block">
                    {{ response.short_code }}
                  </CsText>
                  <CsText
                    variant="body-sm"
                    variant-color="secondary"
                    class="truncate block w-[350px]"
                  >
                    {{ response.content }}
                  </CsText>
                </div>
              </CsListboxItem>
            </CsListbox>
          </div>
        </CsPopoverCardContent>
      </CsPopoverCard>
    </CsPopoverContent>
  </CsPopover>
</template>
